/**
 * @file fileSave.ts
 * Environment-agnostic "save file to disk" with a provider extension point
 * (plugin pattern — core code never imports host-specific save logic; host
 * plugins self-register here, keyed by appEnv, e.g. grabcadPlugin.ts).
 *
 * saveFileAs() routes to the provider registered for the current appEnv, or
 * falls back to a browser <a download> when no provider matches. Exposed to
 * app developers as a method on <as-app>.
 */

import { liveAppData } from '../store/store.ts';
import { devlog } from './utils.ts';

export interface FileSaveResult {
  /** False when the user cancelled the save dialog (providers only — the
   *  browser download fallback cannot detect cancellation). */
  success: boolean;
  /** Path the file was written to, where the provider reports one. */
  filepath?: string;
}

export interface FileSaveOptions {
  /** Native dialog title, where the environment shows one. */
  title?: string;
  /** Extension for the dialog's file filter, without the dot (e.g. 'asproject'). */
  fileExtension?: string;
  /** Human-readable name for the extension filter (e.g. 'App Suite Project'). */
  fileExtensionName?: string;
}

export interface FileSaveProvider {
  /** Save the blob via the host environment's native mechanism. Resolves once
   *  the host reports the result — callers may await it before e.g. closing
   *  the window. */
  saveFile(blob: Blob, filename: string, opts: FileSaveOptions): Promise<FileSaveResult>;
  /**
   * If true, this provider accurately tracks whether the user saved or cancelled
   * (i.e. the save runs through a native host dialog that reports its result).
   * Callers should defer the actual saveFile call to the host's close sequence
   * rather than invoking it standalone — the provider requires that context to
   * show its dialog and receive a response.
   * The browser <a download> fallback leaves this unset (fire-and-forget).
   */
  saveStatusTrackable?: true;
}

const providers = new Map<string, FileSaveProvider>();

/** Register a provider for an appEnv value (e.g. 'grabcad'). */
export function registerFileSaveProvider(env: string, provider: FileSaveProvider): void {
  providers.set(env, provider);
}

/** Returns true if the current environment's save provider is saveStatusTrackable. */
export function isSaveTrackable(): boolean {
  const provider = liveAppData.appEnv ? providers.get(liveAppData.appEnv) : undefined;
  return provider?.saveStatusTrackable === true;
}

/** The save provider matching the given env (defaults to the current appEnv), or
 *  undefined when none is registered (i.e. saveFileAs falls back to a browser
 *  <a download>). Lets callers tell which channel a save actually went through. */
export function getFileSaveProvider(env: string | null = liveAppData.appEnv): FileSaveProvider | undefined {
  return env ? providers.get(env) : undefined;
}

/**
 * Save a file to disk using the mechanism appropriate for the current
 * environment: the registered provider's native flow if one matches
 * liveAppData.appEnv, otherwise a browser <a download>.
 */
export async function saveFileAs(
  blob: Blob,
  filename: string,
  opts: FileSaveOptions = {}
): Promise<FileSaveResult> {
  const provider = liveAppData.appEnv ? providers.get(liveAppData.appEnv) : undefined;
  devlog('[SAVE] saveFileAs: appEnv =', liveAppData.appEnv ?? '(none)', '| provider =', !!provider, '| file =', filename);
  if (provider) return provider.saveFile(blob, filename, opts);

  const url = URL.createObjectURL(blob);
  Object.assign(document.createElement('a'), { href: url, download: filename }).click();
  URL.revokeObjectURL(url);
  return { success: true };
}
